import { useState, useRef, useCallback } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/firebaseConfig';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function generateId() {
  return Date.now() + '-' + Math.random().toString(36).slice(2);
}

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

export function useImageUpload() {
  const [pendingImages, setPendingImages] = useState([]);
  const uploadTasksRef = useRef([]);

  const addImages = useCallback(async (files) => {
    const fileArray = Array.from(files);
    const errors = [];

    // Filter and validate
    const validFiles = [];
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        errors.push(`"${file.name}" is not an image`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" — unsupported image type`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds 5MB limit`);
        continue;
      }
      validFiles.push(file);
    }

    // Check count limit
    setPendingImages(prev => {
      const remaining = MAX_IMAGES - prev.length;
      if (remaining <= 0) {
        errors.push(`Maximum ${MAX_IMAGES} images per message`);
        return prev;
      }
      const toAdd = validFiles.slice(0, remaining);
      if (toAdd.length < validFiles.length) {
        errors.push(`Only ${remaining} more image${remaining > 1 ? 's' : ''} can be added`);
      }

      const newImages = toAdd.map(file => ({
        id: generateId(),
        file,
        previewUrl: URL.createObjectURL(file),
        width: 0,
        height: 0,
        status: 'pending',
        progress: 0,
        error: null
      }));

      // Read dimensions async (updates state after)
      newImages.forEach(async (img) => {
        const dims = await getImageDimensions(img.file);
        setPendingImages(current =>
          current.map(p => p.id === img.id ? { ...p, width: dims.width, height: dims.height } : p)
        );
      });

      return [...prev, ...newImages];
    });

    return errors;
  }, []);

  const removeImage = useCallback((id) => {
    setPendingImages(prev => {
      const img = prev.find(p => p.id === id);
      if (img) {
        URL.revokeObjectURL(img.previewUrl);
      }
      return prev.filter(p => p.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    setPendingImages(prev => {
      prev.forEach(img => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    uploadTasksRef.current = [];
  }, []);

  const uploadImages = useCallback(async (accountUUID, messageId) => {
    const images = [...pendingImages];
    if (images.length === 0) return [];

    uploadTasksRef.current = [];

    const uploadPromises = images.map((img) => {
      return new Promise((resolve, reject) => {
        const storagePath = `supportMessages/${accountUUID}/${messageId}/${img.id}_${img.file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, img.file, {
          contentType: img.file.type
        });

        uploadTasksRef.current.push(uploadTask);

        // Update status to uploading
        setPendingImages(prev =>
          prev.map(p => p.id === img.id ? { ...p, status: 'uploading' } : p)
        );

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setPendingImages(prev =>
              prev.map(p => p.id === img.id ? { ...p, progress } : p)
            );
          },
          (error) => {
            setPendingImages(prev =>
              prev.map(p => p.id === img.id ? { ...p, status: 'error', error: error.message } : p)
            );
            reject(error);
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              setPendingImages(prev =>
                prev.map(p => p.id === img.id ? { ...p, status: 'done', progress: 100 } : p)
              );
              resolve({
                url,
                name: img.file.name,
                size: img.file.size,
                storagePath,
                width: img.width,
                height: img.height
              });
            } catch (error) {
              setPendingImages(prev =>
                prev.map(p => p.id === img.id ? { ...p, status: 'error', error: error.message } : p)
              );
              reject(error);
            }
          }
        );
      });
    });

    return Promise.all(uploadPromises);
  }, [pendingImages]);

  const cancelUploads = useCallback(() => {
    uploadTasksRef.current.forEach(task => {
      try { task.cancel(); } catch (e) { /* ignore */ }
    });
    uploadTasksRef.current = [];
  }, []);

  return {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    cancelUploads,
    hasImages: pendingImages.length > 0
  };
}
